import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { RefreshCw, Trash2, Plus, Loader2 } from "lucide-react";

/**
 * Hantering av bevakade fotbollslag (Min Fotboll-integration).
 *
 * - Listar lag i `football_watched_teams`
 * - Lägger till nytt lag via formulär (klubbnamn + valfri team-label, team_id)
 * - Triggrar manuell sync mot Min Fotboll-API:t (länkar matcher i kalendern)
 * - Visar senaste sync-resultat (linked / created / watches_registered)
 */
export function FootballTeamsSection() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    club_name: "",
    team_label: "",
    minfotboll_team_id: "",
    minfotboll_club_id: "",
    is_home_team: true,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/football/watched-teams");
      setTeams(data?.items || []);
    } catch {
      toast.error("Kunde inte hämta bevakade lag");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async (e) => {
    e?.preventDefault();
    if (!form.club_name.trim()) {
      toast.error("Klubbnamn krävs");
      return;
    }
    setAdding(true);
    try {
      const payload = {
        club_name: form.club_name.trim(),
        team_label: form.team_label.trim() || null,
        is_home_team: form.is_home_team,
      };
      if (form.minfotboll_team_id.trim()) {
        payload.minfotboll_team_id = parseInt(form.minfotboll_team_id, 10);
      }
      if (form.minfotboll_club_id.trim()) {
        payload.minfotboll_club_id = parseInt(form.minfotboll_club_id, 10);
      }
      const { data } = await api.post("/football/watched-teams", payload);
      if (data?.already_exists) {
        toast.info("Laget bevakas redan");
      } else {
        toast.success(`${payload.club_name} tillagd`);
      }
      setForm({ club_name: "", team_label: "", minfotboll_team_id: "", minfotboll_club_id: "", is_home_team: true });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte lägga till lag");
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (teamId, label) => {
    if (!confirm(`Sluta bevaka ${label}?`)) return;
    try {
      await api.delete(`/football/watched-teams/${teamId}`);
      toast.success("Borttaget");
      load();
    } catch {
      toast.error("Kunde inte ta bort");
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const { data } = await api.post("/football/watched-teams/sync");
      setSyncResult(data);
      const totals = (data?.results || []).reduce(
        (acc, r) => {
          acc.linked += r.linked || 0;
          acc.created += r.created || 0;
          acc.watches += r.watches_registered || 0;
          return acc;
        },
        { linked: 0, created: 0, watches: 0 }
      );
      toast.success(
        `Synk klar — ${totals.linked} länkade · ${totals.created} skapade · ${totals.watches} bevakningar`
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Synk misslyckades");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="football-teams-section">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold">Bevakade fotbollslag</h2>
          <p className="text-xs text-[color:var(--text-tertiary)] mt-0.5">
            Auto-länkar matcher i kalendern till Min Fotboll för live-resultat.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          disabled={syncing}
          onClick={handleSync}
          data-testid="football-sync-button"
        >
          {syncing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
          Synka nu
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-[color:var(--text-tertiary)]">Läser in…</p>
      ) : teams.length === 0 ? (
        <p className="text-sm text-[color:var(--text-tertiary)]">
          Inga lag bevakas än. Lägg till nedan.
        </p>
      ) : (
        <ul className="space-y-2">
          {teams.map((t) => (
            <li
              key={t.id}
              className="card-soft p-3 flex items-center justify-between gap-2"
              data-testid={`football-team-${t.id}`}
            >
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">
                  {t.club_name}
                  {t.team_label ? (
                    <span className="text-[color:var(--text-tertiary)] font-normal">
                      {" · "}{t.team_label}
                    </span>
                  ) : null}
                </p>
                <p className="text-[11px] text-[color:var(--text-tertiary)]">
                  {t.minfotboll_team_id
                    ? `TeamID ${t.minfotboll_team_id}`
                    : "Inget team_id — bara titel-matchning"}
                  {t.is_home_team ? " · barnens lag" : ""}
                </p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleRemove(t.id, `${t.club_name}${t.team_label ? " · " + t.team_label : ""}`)}
                data-testid={`football-team-remove-${t.id}`}
                aria-label="Ta bort"
              >
                <Trash2 className="w-4 h-4 text-red-600" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {syncResult ? (
        <div className="card-soft p-3 text-xs space-y-1" data-testid="football-sync-result">
          <p className="font-medium">Senaste synk</p>
          {(syncResult.results || []).map((r) => (
            <p key={r.team_id} className="text-[color:var(--text-tertiary)]">
              <span className="text-[color:var(--text-primary)] font-medium">{r.team_name}</span>
              {" — "}
              {r.games_seen} matcher · {r.linked} länkade · {r.created} skapade · {r.watches_registered} bevakningar
            </p>
          ))}
        </div>
      ) : null}

      <form onSubmit={handleAdd} className="card-soft p-3 space-y-3" data-testid="football-team-add-form">
        <p className="text-sm font-medium">Lägg till lag</p>
        <div className="grid grid-cols-1 gap-2">
          <Input
            placeholder="Klubbnamn (t.ex. Djurö-Vindö IF)"
            value={form.club_name}
            onChange={(e) => setForm({ ...form, club_name: e.target.value })}
            data-testid="football-team-club-name"
          />
          <Input
            placeholder="Lag-etikett (valfritt, t.ex. PF12/13/14)"
            value={form.team_label}
            onChange={(e) => setForm({ ...form, team_label: e.target.value })}
            data-testid="football-team-label"
          />
          <div className="grid grid-cols-2 gap-2">
            <Input
              placeholder="Min Fotboll TeamID"
              type="number"
              value={form.minfotboll_team_id}
              onChange={(e) => setForm({ ...form, minfotboll_team_id: e.target.value })}
              data-testid="football-team-id"
            />
            <Input
              placeholder="ClubID (valfritt)"
              type="number"
              value={form.minfotboll_club_id}
              onChange={(e) => setForm({ ...form, minfotboll_club_id: e.target.value })}
              data-testid="football-club-id"
            />
          </div>
          <label className="flex items-center justify-between text-sm">
            <span>Barnens lag (visa primärt)</span>
            <Switch
              checked={form.is_home_team}
              onCheckedChange={(v) => setForm({ ...form, is_home_team: v })}
              data-testid="football-team-is-home"
            />
          </label>
        </div>
        <Button type="submit" size="sm" disabled={adding} data-testid="football-team-add-button">
          {adding ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
          Lägg till
        </Button>
        <p className="text-[11px] text-[color:var(--text-tertiary)] leading-snug">
          Hitta TeamID i Min Fotboll-appen genom att öppna lagsidan — sista siffrorna i URL:en är TeamID. Utan TeamID görs bara titel-matchning mot SportsAdmin-events (ingen live-data).
        </p>
      </form>
    </div>
  );
}
